<template>
  <f7-page name="seller-profile" @page:show="onShow">
    <f7-navbar :title="$t('sellerProfile.title')" back-link no-shadow>
    </f7-navbar>
    <AppLayout>
      <template #main>
        <f7-block v-if="savedProfile" class="selp-summary">
          <f7-card outline class="selp-summary-card">
            <div class="selp-summary-row">
              <div class="selp-avatar">
                <img v-if="savedProfile.logoUrl" :src="savedProfile.logoUrl" alt="" />
                <f7-icon v-else f7="person_fill" size="22" />
              </div>
              <div class="selp-summary-info">
                <div class="selp-name">
                  {{ savedProfile.displayName || savedProfile.businessName }}
                </div>
                <div class="selp-sub">
                  <span class="selp-verified">
                    <f7-icon f7="checkmark_shield_fill" size="11" />
                    {{ $t('sellerHub.verified') }}
                  </span>
                  <span v-if="savedProfile.sellerType">
                    · {{ savedProfile.sellerType === 'BUSINESS' ? $t('sellerHub.sellerTypeBusiness') : $t('sellerHub.sellerTypeIndividual') }}
                  </span>
                </div>
              </div>
            </div>
          </f7-card>
          <p class="selp-note">{{ $t('sellerProfile.profileSavedNote') }}</p>
        </f7-block>

        <f7-block-title>{{ savedProfile ? $t('sellerProfile.editTitle') : $t('sellerProfile.title') }}</f7-block-title>
        <f7-list inset strong media-list outline dividers>
          <f7-list-item :title="$t('sellerProfile.sellerType')">
            <template #after>
              <f7-segmented>
                <f7-button
                  small round raised
                  :class="profile.sellerType !== 'BUSINESS' ? 'selp-seg-active' : ''"
                  @click="profile.sellerType = 'INDIVIDUAL'"
                >
                  {{ $t('sellerHub.sellerTypeIndividual') }}
                </f7-button>
                <f7-button
                  small round raised
                  :class="profile.sellerType === 'BUSINESS' ? 'selp-seg-active' : ''"
                  @click="profile.sellerType = 'BUSINESS'"
                >
                  {{ $t('sellerHub.sellerTypeBusiness') }}
                </f7-button>
              </f7-segmented>
            </template>
          </f7-list-item>
          <f7-list-input
            type="text"
            :label="$t('sellerProfile.displayName')"
            :value="profile.displayName"
            @input="profile.displayName = $event.target.value"
          />
          <f7-list-input
            v-if="profile.sellerType === 'BUSINESS'"
            type="text"
            :label="$t('sellerProfile.businessName')"
            :value="profile.businessName"
            @input="profile.businessName = $event.target.value"
          />
          <f7-list-input
            type="textarea"
            resizable
            :label="$t('sellerProfile.description')"
            :value="profile.description"
            @input="profile.description = $event.target.value"
          />
          <f7-list-input
            type="text"
            :label="$t('sellerProfile.location')"
            :value="profile.location"
            @input="profile.location = $event.target.value"
          />
          <f7-list-input
            type="tel"
            inputmode="tel"
            ref="phoneInputRef"
            :label="$t('sellerProfile.phone')"
            :value="profile.phone"
            @input="profile.phone = $event.target.value"
          />
          <f7-list-input
            type="text"
            :label="$t('sellerProfile.websiteUrl')"
            :value="profile.websiteUrl"
            @input="profile.websiteUrl = $event.target.value"
          />
        </f7-list>

        <f7-block>
          <f7-button large fill raise round @click="saveProfile" :disabled="profileSaving">
            {{ $t('sellerProfile.saveProfile') }}
          </f7-button>
        </f7-block>

        <f7-block v-if="savedProfile" class="selp-next">
          <f7-button large outline round raised @click="goListings">
            <f7-icon f7="plus" size="16" />
            {{ $t('sellerProfile.createListing') }}
          </f7-button>
        </f7-block>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { reactive, ref, computed } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../common/AppLayout.vue";
import { useMarketplaceProductStore } from "../../js/pinia-stores/marketplaceProduct.js";
import { useKeycloakStore } from "../../js/pinia-stores/keycloak.js";
import {useAccountStore} from "../../js/pinia-stores/account.js";
import { useTelephone } from "../../js/composables/useTelephone.js";

import i18n from "../../js/i18n.js";

const { t } = i18n.global;
const mpStore = useMarketplaceProductStore();
const keycloakStore = useKeycloakStore();
const accountStore=useAccountStore();
const PROFILE_KEY = "avo_seller_profile";
const phoneInputRef = ref(null);
useTelephone(phoneInputRef);
const userAccount=computed(() => accountStore.userAccount);
const userProfile=computed(()=>keycloakStore.profile);

const savedProfile = computed(() => {
  try {
    return JSON.parse(localStorage.getItem(PROFILE_KEY) || "null");
  } catch (e) {
    return null;
  }
});
const address = computed(() => {
  const a = userAccount.value?.standard?.address;
  if (!a) return "";
  return [a.label1, a.label2, a.zip_code, a.city, a.state, a.country]
    .filter(Boolean)
    .join(" ");
});
const profileSaving = ref(false);
const profile = reactive({
  sellerType: "INDIVIDUAL",
  displayName: userAccount.value?.name || userProfile.value?.name || "",
  businessName: "",
  description: "",
  location: address.value || "",
  phone: userAccount.value?.username || userProfile.value?.username || "",
  websiteUrl: "",
});

function fillFromSaved() {
  const saved = savedProfile.value;
  profile.sellerType = saved?.sellerType || "INDIVIDUAL";
  profile.displayName =
    saved?.displayName ||
    keycloakStore.userInfo?.name ||
    keycloakStore.userInfo?.given_name ||
    "";
  profile.businessName = saved?.businessName || "";
  profile.description = saved?.description || "";
  profile.location = saved?.location || "";
  profile.phone = saved?.phone || "";
  profile.websiteUrl = saved?.websiteUrl || "";
}

function onShow() {
  fillFromSaved();
}

async function saveProfile() {
  profileSaving.value = true;
  try {
    const payload = {
      sellerType: profile.sellerType,
      displayName: profile.displayName.trim(),
      businessName: profile.businessName.trim(),
      description: profile.description.trim(),
      location: profile.location.trim(),
      phone: profile.phone.trim(),
      websiteUrl: profile.websiteUrl.trim(),
    };
    const saved = await mpStore.sellerProfile(payload);
    localStorage.setItem(PROFILE_KEY, JSON.stringify(saved || payload));
    f7.toast.show({ text: t("sellerProfile.saved"), closeTimeout: 2000 });
  } catch (e) {
    f7.dialog.alert(
      e?.response?.data?.message || e?.message || t("sellerProfile.saveError"),
      t("common.error"),
    );
  } finally {
    profileSaving.value = false;
  }
}

function goListings() {
  f7.views.main.router.navigate("/sell/listings");
}
</script>

<style scoped>
.selp-summary-card {
  border-radius: 12px;
}
.selp-summary-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.selp-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--f7-list-bg-color, #f0f0f2);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}
.selp-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.selp-summary-info {
  flex: 1;
  min-width: 0;
}
.selp-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--f7-text-color, #333);
}
.selp-sub {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin-top: 2px;
}
.selp-verified {
  color: var(--avocash-green, #1a6b42);
  font-weight: 600;
}
.selp-note {
  font-size: 13px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin-top: 10px;
}
.selp-next {
  margin-top: 4px;
}
.selp-seg-active {
  background: color-mix(in srgb, var(--f7-theme-color) 14%, transparent);
}
</style>